/* eslint-disable @typescript-eslint/no-explicit-any */
import { isValidElement, type ReactNode } from 'react';

/**
 * Shared building blocks for read-only detail/view screens.
 *
 * `Field` renders a labelled value cell; `Section` is the white card that groups
 * them. Kept in one place so every master's detail page (functions, value sets,
 * error/log master, …) shows fields the same way instead of each re-inventing the
 * label + `<p>` pattern. Pair with `AuditInfoCard` for created/updated metadata.
 */

interface FieldProps {
  label: string;
  value: any;
  /** Render the value in a monospace font (codes, ids, paths). */
  mono?: boolean;
  /** Pretty-print an object/array value as JSON in a scrollable block. */
  json?: boolean;
  /** Draw the value in amber — a soft "needs attention" (e.g. a legacy field). */
  warn?: boolean;
  /** Span both columns of a 2-column Section (long text, tables). */
  wide?: boolean;
}

/** A labelled value cell. Empty values render a neutral dash; a React element
 *  value (e.g. a <Tag/> or link) is rendered as-is so a field can hold a chip. */
export const Field = ({ label, value, mono, json, warn, wide }: FieldProps) => {
  const empty = value === null || value === undefined || value === '';

  let body: ReactNode;
  if (empty) {
    body = <span className="text-sm text-ink-muted">-</span>;
  } else if (isValidElement(value)) {
    body = <div className="text-sm text-ink">{value}</div>;
  } else if (json) {
    body = (
      <pre className="text-sm text-ink whitespace-pre-wrap break-words font-mono bg-canvas dark:bg-gray-900 rounded p-2 max-h-56 overflow-auto">
        {JSON.stringify(value, null, 2)}
      </pre>
    );
  } else {
    body = (
      <p className={`text-sm break-words whitespace-pre-wrap ${warn ? 'text-warning' : 'text-ink'} ${mono ? 'font-mono' : ''}`}>
        {String(value)}
      </p>
    );
  }

  return (
    <div className={wide ? 'sm:col-span-2' : undefined}>
      <label className="text-xs text-ink-muted block mb-0.5">{label}</label>
      {body}
    </div>
  );
};

/** Soft, low-saturation status chip. One consistent look for detail screens —
 *  replaces the loud PrimeReact `<Tag>` severities (especially the dark
 *  `secondary`) so status colours read as calm tints, not heavy blocks. */
export type PillTone = 'neutral' | 'blue' | 'green' | 'amber' | 'red';

const PILL_TONES: Record<PillTone, string> = {
  neutral: 'bg-slate-100 text-slate-600 ring-slate-200 dark:bg-gray-700/60 dark:text-gray-300 dark:ring-gray-600',
  blue: 'bg-blue-50 text-blue-700 ring-blue-100 dark:bg-blue-900/25 dark:text-blue-300 dark:ring-blue-900/40',
  green: 'bg-emerald-50 text-emerald-700 ring-emerald-100 dark:bg-emerald-900/25 dark:text-emerald-300 dark:ring-emerald-900/40',
  amber: 'bg-amber-50 text-amber-700 ring-amber-100 dark:bg-amber-900/25 dark:text-amber-300 dark:ring-amber-900/40',
  red: 'bg-rose-50 text-rose-700 ring-rose-100 dark:bg-rose-900/25 dark:text-rose-300 dark:ring-rose-900/40',
};

export const Pill = ({ tone = 'neutral', className, children }: { tone?: PillTone; className?: string; children: ReactNode }) => (
  <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${PILL_TONES[tone]} ${className || ''}`}>
    {children}
  </span>
);

interface SectionProps {
  title: string;
  children: ReactNode;
  /** Column count for the field grid; 1 for wide content (tables, JSON). */
  cols?: 1 | 2;
  /** Extra classes (e.g. to span grid columns in a parent grid). */
  className?: string;
  /** Optional right-aligned header content (e.g. a count tag or action). */
  actions?: ReactNode;
}

/** A titled white card wrapping a responsive grid of `Field`s (or any content). */
export const Section = ({ title, children, cols = 2, className, actions }: SectionProps) => (
  <div className={`ds-card p-4 ${className || ''}`}>
    <div className="flex items-center justify-between mb-2">
      <h2 className="text-sm font-semibold text-ink">{title}</h2>
      {actions}
    </div>
    <div className={`grid grid-cols-1 ${cols === 2 ? 'sm:grid-cols-2' : ''} gap-x-4 gap-y-3`}>{children}</div>
  </div>
);
